import React, { useState } from 'react';
import { Tooltip } from '..';
import Spinner from 'src/assets/images/spinner.svg';
import { twMerge } from 'tailwind-merge';

interface ButtonProps {
  variant: 'normal' | 'primary' | 'secondary' | 'text' | 'cancel';
  size: 'xs' | 'sm' | 'md' | 'lg';
  label: string;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'reset' | 'submit' | 'fileUpload';
  onFileUpload?: (event: File) => void;
  form?: string;
  showTooltip?: boolean;
  icon?: string;
  onIconHover?: () => void;
  onIconHoverOut?: () => void;
  tooltipContainerClass?: string;
  labelClassName?: string;
  isLoading?: boolean;
  loaderClassName?: string;
  roundness?: string;
}

function Button({
  variant,
  size,
  label,
  onClick,
  className,
  disabled,
  type,
  form,
  onFileUpload,
  showTooltip,
  icon,
  onIconHover,
  onIconHoverOut,
  tooltipContainerClass,
  labelClassName,
  isLoading,
  loaderClassName,
  roundness = 'rounded-full',
}: ButtonProps) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const inputFileRef = React.useRef<any>(null);
  let btnClasses = {
    normal:
      'bg-lightBlue text-white justify-center items-center gap-2.5 inline-flex hover:bg-darkBlue hover:shadow-[0_1px_10px_0px_rgba(85,87,201,0.40)]',
    secondary:
      'bg-white border border-lightBlue text-lightBlue justify-center items-center gap-2.5 inline-flex hover:bg-violet-100',
    primary:
      'bg-gradient-to-l from-white to-white border border-greenPositive rounded-lg text-greenPositive bg-origin-border hover:border-transparent hover:shadow-[0_2px_4px_rgba(30, 190, 113,0.2)] hover:from-greenPositive hover:to-greenPositive hover:text-[#fff] active:border-transparent active:from-greenPositive active:to-greenPositive active:text-white active:shadow-[0_2px_4px_rgba(30, 190, 113,0.2)] disabled:border-[#231f204d] disabled:text-[#231f204d] disabled:shadow-none disabled:from-white disabled:to-white',
    text: 'bg-gradient-to-l from-flamingo to-flamingo text-transparent bg-clip-text hover:to-rubyShard active:from-rubyShard active:to-rubyShard disabled:from-[#231f2026] disabled:to-[#231f2026]',
    cancel:
      'from-white to-white border border-[#231F2080] rounded-lg text-[#231F2080] bg-origin-border  disabled:border-[#231f2026] disabled:text-[#231f2026] disabled:shadow-none disabled:from-transparent disabled:to-transparent',
  };
  let sizeClasses = {
    xs: 'font-12 px-3 pt-2 pb-1 leading-2',
    sm: 'font-14 px-4 pt-[5px] pb-[7px] leading-2',
    md: 'font-16 px-[24px] pt-[13px] pb-[10px] leading-2',
    lg: 'font-18 px-[24px] pt-[18px] pb-[15px] leading-2',
  };

  const onFileUploadHandler = (event: any) => {
    onFileUpload && onFileUpload(event);
  };

  const onClickHandler = (event: any) => {
    if (type === 'fileUpload') {
      inputFileRef.current.value = '';
      return inputFileRef.current?.click();
    }
    onClick && onClick();
  };
  return (
    <>
      {showTooltip ? (
        <Tooltip
          showArrow={false}
          trigger={['hover']}
          overlay={
            <span className="font-SFProDisplay font-normal text-xs text-[#231f20cc]">
              You are not authorized to perform this action. Please contact your
              system admin.
            </span>
          }
          overlayClassName="cus-button-tooltip"
          childContainerClass="w-full"
          placement="top"
          visible={isTooltipOpen}
          containerClass={tooltipContainerClass}
        >
          <button
            form={form}
            className={`${
              className ? ` ${className}` : ''
            } font-SFProDisplay uppercase transition-all disabled:cursor-not-allowed disabled:bg-[#231F204D] disabled:shadow-none m-1 ${
              btnClasses[variant]
            } ${sizeClasses[size]}`}
            onClick={onClickHandler}
            disabled={disabled}
            type={type !== 'fileUpload' ? type : undefined}
            onMouseOver={onIconHover}
            onMouseOut={onIconHoverOut}
            onPointerEnter={() => {
              setIsTooltipOpen(() => true);
            }}
            onPointerLeave={() => {
              setTimeout(() => setIsTooltipOpen(() => false), 1000);
            }}
          >
            {icon && (
              <img src={icon} alt="icon" className="mr-2 mb-1 inline-block" />
            )}
            {label}
          </button>
        </Tooltip>
      ) : (
        <>
          <button
            form={form}
            className={`${
              className ? ` ${className}` : ''
            } font-SFProDisplay transition-all disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none m-1 h-12 px-9 py-[15px] ${roundness}   ${
              btnClasses[variant]
            } ${sizeClasses[size]} `}
            onClick={onClickHandler}
            disabled={disabled}
            type={type !== 'fileUpload' ? type : undefined}
            onMouseOver={onIconHover}
            onMouseOut={onIconHoverOut}
          >
            {icon && (
              <img src={icon} alt="icon" className="mr-2 mb-1 inline-block" />
            )}
            {isLoading && (
              <img
                src={Spinner}
                className={twMerge('animate-spin', loaderClassName)}
                alt="spinner"
              />
            )}

            {!isLoading && (
              <span
                className={twMerge(
                  'font-SFProDisplay  text-sm font-medium',
                  labelClassName
                )}
              >
                {label}
              </span>
            )}
          </button>
          {type === 'fileUpload' && (
            <input
              type="file"
              className="hidden"
              ref={inputFileRef}
              onChange={onFileUploadHandler}
            />
          )}
        </>
      )}
    </>
  );
}

export default Button;
